import React, { useEffect, useState } from 'react';
import { useSalesPartnerData } from '../../SalesPartnerData';
import { SalesPartnerPaymentModel } from 'src/models/salesPartner/SalesPartnerPaymentModel';
import SearchAppBar from 'src/components/VemigoSearchBar/SearchBar';
import VemigoGrid from 'src/components/VemigoGrid';
import { GridColDef } from '@mui/x-data-grid';
import { searchRows } from 'src/components/VemigoSearchBar/searchFunction';
import { SalesPartnerPaymentServices } from 'src/services/salesPartner/salesPartnerPaymentServices';
import PaymentHistoryOptions from './PaymentHistoryOptions';
import { AddPaymentHistory } from './AddPaymentHistory';
import { Button, Grid, Typography, CircularProgress } from '@mui/material';
import { VemigoDateRangeField } from 'src/components/VemigoGrid/VemigoDateRangeField';
import { enqueueSnackbar } from 'notistack';

const SalesPartnerPaymentHistoryList = () => {
    const { salesPartner, bankAccountsList = [] } = useSalesPartnerData();
    const [filteredRows, setFilteredRows] = useState<SalesPartnerPaymentModel[]>([]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [showCalendar, setShowCalendar] = useState(false);
    const [initialRows, setInitialRows] = useState<SalesPartnerPaymentModel[]>([]);
    const paymentModes = [
        { id: 0, name: 'Online' },
        { id: 1, name: 'Cash' }
    ];

    const getBankAccountName = (id: number) => {
        const bankList = Array.isArray(bankAccountsList) ? bankAccountsList : [];
        const match = bankList.find((bank) => bank.id === id);
        return match ? match.bankName : 'No Account';
    };

    const parseDateString = (dateString: string): Date | null => {
        if (!dateString) return null;
        
        if (dateString.includes('/')) {
            const parts = dateString.split('/');
            if (parts.length !== 3) return null;
            
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }
        
        try {
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                return date;
            }
        } catch (error) {
            console.error('Error parsing date:', error);
        }
        
        return null;
    };

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: '#',
            flex: 0.1,
            headerAlign: 'left'
        },
        {
            field: 'date',
            headerName: 'Date',
            flex: 1,
            headerAlign: 'left',
            renderCell: (params) => {
                if (!params.value) return 'N/A';
                
                const parsedDate = parseDateString(params.value);
                if (parsedDate) {
                    return parsedDate.toLocaleDateString('en-GB');
                }
                
                try {
                    const date = new Date(params.value);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('en-GB');
                    }
                } catch (error) {
                    console.error('Error parsing date:', error);
                }
                
                return 'Invalid Date';
            }
        },
        {
            field: 'paymentMode',
            headerName: 'Payment Mode',
            flex: 1,
            headerAlign: 'left',
            renderCell: (params) => {
                const value = Number(params.value);
                if (value === 0) return 'Online';
                if (value === 1) return 'Cash';
                return 'N/A';
            }
        },
        {
            field: 'spBankAccountId',
            headerName: 'Account Details',
            flex: 1,
            headerAlign: 'left',
            renderCell: (params) => getBankAccountName(params.value)
        },
        {
            field: 'amount',
            headerName: 'Amount',
            flex: 1,
            headerAlign: 'center',
            align: 'center'
        },
        {
            field: 'paidByName',
            headerName: 'Paid By',
            flex: 1,
            headerAlign: 'left'
        },
        {
            field: 'voucherNumber',
            headerName: 'Voucher Number',
            flex: 1,
            headerAlign: 'center',
            align: 'left'
        },
        {
            field: 'edit',
            headerName: '',
            flex: 0.3,
            sortable: false,
            renderCell: (params) => <PaymentHistoryOptions rowData={params.row} onEditSuccess={handleEditSuccess} />
        }
    ];

    const openAddDataGrid = () => {
        setOpen(true);
    };

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value.toLowerCase();
        const filtered = searchRows(text, initialRows);
        setFilteredRows(filtered);
    };

    const handleDateChange = (dates: string[]) => {
        setSelectedDates(dates);
        
        if (dates.length === 2) {
            setShowCalendar(false);
        }
    };

    const handleGetData = () => {
        if (!salesPartner?.id) {
            enqueueSnackbar('Please select a sales partner', { variant: 'error' });
            return;
        }

        if (selectedDates.length < 2) {
            if (selectedDates[0] === undefined) {
                enqueueSnackbar('Please select the Date Range', { variant: 'error' });
                return;
            } else {
                enqueueSnackbar(`Please select the Start/End Date for ${selectedDates[0]}`, {
                    variant: 'error'
                });
                return;
            }
        }

        setIsLoading(true);

        const [d, m, y] = selectedDates[0].split('/').map(Number);
        const [dd, mm, yy] = selectedDates[1].split('/').map(Number);
        const startDate = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const endDate = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;

        SalesPartnerPaymentServices.getPaymentsBySalesPartnerIdAndDateRange(salesPartner.id, startDate, endDate)
            .then((data) => {
                const modList = data.map((item: SalesPartnerPaymentModel) => ({
                    ...item,
                    id: item.id,
                    date: item.date || ''
                }));
                setInitialRows(modList);
                setFilteredRows(modList);
                enqueueSnackbar(`Found ${modList.length} payment records`, { variant: 'success' });
            })
            .catch((error) => {
                console.error('Error fetching payment history:', error);
                enqueueSnackbar('Error fetching payment history', { variant: 'error' });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleEditSuccess = (updatedRecord: SalesPartnerPaymentModel) => {
        setFilteredRows(prevRows =>
            prevRows.map(row =>
                row.id === updatedRecord.id ? updatedRecord : row
            )
        );
    };

    useEffect(() => {
        if (salesPartner?.id) {
            setInitialRows([]);
            setFilteredRows([]);
            setSelectedDates([]);
        }
    }, [salesPartner?.id]);

    return (
        <>
            <Grid>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '1.5rem',
                        padding: '8px',
                        width: '100%',
                        flexWrap: 'wrap'
                    }}
                >
                    <div style={{ minWidth: '280px', width: '300px' }}>
                        <Typography variant="h4" style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                            Date Range
                        </Typography>
                        <VemigoDateRangeField
                            width="100%"
                            selectedSEDates={selectedDates}
                            setSelectedSEDates={handleDateChange}
                            show={showCalendar}
                            setShow={setShowCalendar}
                        />
                    </div>

                    <div>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                height: '55px',
                                width: '90px',
                                '&.Mui-disabled': {
                                    backgroundColor: 'primary.main',
                                    color: 'white'
                                }
                            }}
                            onClick={handleGetData}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={15} color="inherit" /> : null}
                        >
                            {isLoading ? 'Loading..' : 'Go'}
                        </Button>
                    </div>
                </div>
            </Grid>

            <SearchAppBar
                handleChange={handleSearchTextChange}
                addData={openAddDataGrid}
                buttonText="+ Add Payment"
            >
                <VemigoGrid rows={filteredRows} columns={columns} />
            </SearchAppBar>

            <AddPaymentHistory
                open={open}
                handleClose={() => setOpen(false)}
                onDataAdded={(newRecord) => {
                    setFilteredRows((prev) => [...prev, newRecord]);
                    setInitialRows((prev) => [...prev, newRecord]);
                }}
            />
        </>
    );
};

export default SalesPartnerPaymentHistoryList;